'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import SessionUploadPopup from './SessionUploadPopup';

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignUpFormValues {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [pendingSignUpData, setPendingSignUpData] = useState<SignUpFormValues | null>(null);
  const [signUpForSubscription, setSignUpForSubscription] = useState(false);
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const [newUserUploader, setNewUserUploader] = useState<{ userId: string; userEmail: string; userName: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const {
    register: registerSignUp,
    handleSubmit: handleSignUpSubmit,
    formState: { errors: signUpErrors },
    watch: watchSignUp,
    reset: resetSignUp,
  } = useForm<SignUpFormValues>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '' },
  });

  const signUpPassword = watchSignUp('password');

  const redirectToStripeCheckout = async (userId: string, email: string, fullName?: string, accessToken?: string) => {
    setIsRedirectingToStripe(true);
    try {
      const supabase = createClient();
      // Use provided token first, then try getSession
      let token = accessToken;
      if (!token) {
        const { data: { session } } = await supabase.auth.getSession();
        token = session?.access_token;
      }

      if (!token) {
        throw new Error('No active session. Please sign in first.');
      }

      const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({
            userId,
            email,
            fullName: fullName || '',
            successUrl: `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/login`,
          }),
        }
      );
      const result = await response.json();
      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error(result.error || 'Failed to create checkout session');
      }
    } catch (err: any) {
      setIsRedirectingToStripe(false);
      toast.error(err?.message || 'Could not start checkout. Please try again.');
    }
  };

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);

    const supabase = createClient();

    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) throw error;

      let resolvedRole: 'admin' | 'peak_psych' | 'external' | 'peak_member' = 'peak_member';
      let resolvedSubscriptionStatus: string = 'inactive';
      try {
        const userId = authData?.user?.id;
        if (userId) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('role, subscription_status')
            .eq('id', userId)
            .single();
          if (
            profile?.role === 'admin' ||
            profile?.role === 'peak_psych' ||
            profile?.role === 'external' ||
            profile?.role === 'peak_member'
          ) {
            resolvedRole = profile.role;
          }
          // Admins and peak_psych users are always treated as active (free access)
          if (profile?.role === 'admin' || profile?.role === 'peak_psych') {
            resolvedSubscriptionStatus = 'active';
          } else {
            resolvedSubscriptionStatus = profile?.subscription_status ?? 'inactive';
          }
        }
      } catch {
        // ignore profile lookup errors
      }

      try {
        localStorage.setItem('userRole', resolvedRole);
        const emailForStorage = authData?.user?.email || data.email;
        if (emailForStorage) localStorage.setItem('userEmail', emailForStorage);
      } catch {
        // ignore storage errors
      }

      toast.success('Welcome back! Signing you in.');

      await new Promise((resolve) => setTimeout(resolve, 300));

      // Gate: redirect to subscribe page if subscription is not active
      if (resolvedRole === 'peak_member' && resolvedSubscriptionStatus !== 'active') {
        window.location.replace('/subscribe');
      } else {
        window.location.replace('/sessionvault');
      }
    } catch (err: any) {
      setIsLoading(false);
      const msg = err?.message || '';
      if (
        msg.toLowerCase().includes('invalid login') ||
        msg.toLowerCase().includes('invalid credentials') ||
        msg.toLowerCase().includes('email not confirmed')
      ) {
        toast.error('Invalid credentials. Please check your email and password.');
      } else {
        toast.error(msg || 'Sign in failed. Please try again.');
      }
    }
  };

  const onSignUp = async (data: SignUpFormValues) => {
    // Show email confirmation popup first
    setPendingSignUpData(data);
  };

  const confirmAndCreateAccount = async () => {
    if (!pendingSignUpData) return;
    const data = pendingSignUpData;
    setPendingSignUpData(null);
    setIsSigningUp(true);
    const supabase = createClient();

    try {
      const initials = data.fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
            avatar_initials: initials,
            role: 'peak_member',
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      const userId = authData?.user?.id;
      if (!userId) throw new Error('Account creation failed. Please try again.');

      if (signUpForSubscription) {
        toast.success('Account created! Taking you to checkout…');

        // Try to get access token from signup response first
        let accessToken = authData?.session?.access_token;

        // If no session from signup (e.g. email confirmation required by Supabase config),
        // sign in immediately to get a valid session token for Stripe
        if (!accessToken) {
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: data.email,
            password: data.password,
          });
          if (!signInError && signInData?.session?.access_token) {
            accessToken = signInData.session.access_token;
          }
        }

        await redirectToStripeCheckout(userId, data.email, data.fullName, accessToken);
      } else {
        toast.success('Account created! Taking you to upload sessions…');
        setShowSignUp(false);
        setIsSigningUp(false);
        window.location.replace('/subscribe');
      }
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        toast.error('An account with this email already exists.');
      } else {
        toast.error(msg || 'Sign up failed. Please try again.');
      }
      setIsSigningUp(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Mobile logo */}
      <div className="flex items-center gap-2 lg:hidden mb-2">
        <img
          src="/assets/images/WHATSAPP_PROFILE_PHOTOS__34_-1782925525799.png"
          alt="PEAK Vault Logo"
          style={{ height: '36px', width: 'auto', objectFit: 'contain' }}
        />
      </div>

      {/* Raised sign-in card */}
      <div
        className="rounded-2xl p-7"
        style={{
          backgroundColor: 'hsl(145 38% 90%)',
          boxShadow: '0 8px 32px hsl(145 40% 30% / 0.18), 0 2px 8px hsl(145 40% 30% / 0.10)',
          border: '1px solid hsl(145 35% 78%)',
        }}
      >
        <div className="mb-5">
          <h2 className="text-2xl font-bold mb-1" style={{ color: '#416478' }}>
            Sign In
          </h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {/* Email */}
          <div>
            <label className="label" htmlFor="email" style={{ color: '#416478' }}>
              Email Address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className="input-field"
              placeholder="you@peakpsychology.co.uk"
              {...register('email', {
                required: 'Email address is required',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
              })}
              style={errors.email ? { borderColor: 'hsl(0 72% 55%)', boxShadow: '0 0 0 3px hsl(0 72% 50% / 0.12)' } : {}}
            />
            {errors.email && (
              <p className="mt-1.5 text-xs font-medium" style={{ color: 'hsl(0 72% 60%)' }}>
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="label" htmlFor="password" style={{ color: '#416478' }}>
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="input-field pr-11"
                placeholder="Enter your password"
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Password must be at least 6 characters' },
                })}
                style={errors.password ? { borderColor: 'hsl(0 72% 55%)', boxShadow: '0 0 0 3px hsl(0 72% 50% / 0.12)' } : {}}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors z-10"
                style={{ color: '#416478', zIndex: 10 }}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'hsl(150 52% 35%)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#416478'; }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1.5 text-xs font-medium" style={{ color: 'hsl(0 72% 60%)' }}>
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 text-base"
            style={{ minHeight: '48px' }}
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Signing in…
              </>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <span>Sign in to</span>
                <img
                  src="/assets/images/WHATSAPP_PROFILE_PHOTOS__34_-1782925525799.png"
                  alt="PEAK Bank Logo"
                  style={{ height: '36px', width: 'auto', objectFit: 'contain' }}
                />
              </span>
            )}
          </button>
        </form>

        {/* Sign Up link */}
        <div className="mt-4 text-center">
          <span className="text-sm" style={{ color: '#416478' }}>
            Don&apos;t have an account?{' '}
          </span>
          <button
            type="button"
            onClick={() => { setSignUpForSubscription(false); setShowSignUp(true); }}
            className="text-sm font-semibold underline underline-offset-2 transition-colors"
            style={{ color: '#416478' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'hsl(150 52% 35%)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#416478'; }}
          >
            Sign Up
          </button>
        </div>

        {/* Subscribe / paid sign-up button — TEMPORARILY HIDDEN (do not delete; restore when paid tier launches) */}
        {/* <div className="mt-3">
          <button
            type="button"
            onClick={() => {
              // Always show sign-up form first — user must create an account before paying
              setSignUpForSubscription(true);
              setShowSignUp(true);
            }}
            disabled={isRedirectingToStripe}
            className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-150 flex items-center justify-center gap-2"
            style={{
              backgroundColor: 'hsl(145 50% 35%)',
              color: 'white',
              border: '2px solid white',
              outline: '1px solid hsl(145 55% 28%)',
              minHeight: '48px',
            }}
            onMouseEnter={(e) => { if (!isRedirectingToStripe) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 55% 28%)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 50% 35%)'; }}
          >
            {isRedirectingToStripe ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Taking you to checkout…
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
                  <path d="M2 10h20" stroke="white" strokeWidth="2"/>
                </svg>
                Subscribe to PEAK Vault
              </>
            )}
          </button>
        </div> */}

        {/* Can't log in link */}
        <div className="mt-2 text-center">
          <button
            type="button"
            onClick={() => setShowContact(true)}
            className="text-sm underline underline-offset-2 transition-colors"
            style={{ color: '#416478', opacity: 0.75 }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.75'; }}
          >
            Can&apos;t log in?
          </button>
        </div>
      </div>

      {/* Contact Modal */}
      {showContact && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          onClick={() => setShowContact(false)}
        >
          <div
            className="relative w-full max-w-md rounded-2xl p-8 shadow-2xl"
            style={{ backgroundColor: 'hsl(145 40% 96%)', border: '1px solid hsl(145 35% 78%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowContact(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg transition-colors"
              style={{ color: 'hsl(145 30% 35%)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 40% 80%)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <h2 className="text-lg font-bold mb-1" style={{ color: 'hsl(145 55% 22%)' }}>
              Contact Us
            </h2>
            <div className="space-y-3 text-sm leading-relaxed" style={{ color: 'hsl(145 20% 30%)' }}>
              <p>For any questions, support or to get logged in, please get in touch with us:</p>
              <p>
                <a
                  href="mailto:matt@peakpsychology.co.uk"
                  className="font-semibold underline"
                  style={{ color: 'hsl(145 50% 30%)' }}
                >
                  matt@peakpsychology.co.uk
                </a>
              </p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowContact(false)}
                className="px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
                style={{
                  backgroundColor: 'hsl(145 50% 35%)',
                  color: 'white',
                  border: '2px solid white',
                  outline: '1px solid hsl(145 55% 28%)',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 55% 28%)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 50% 35%)'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign Up Modal */}
      {showSignUp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
        >
          <div
            className="w-full max-w-md rounded-2xl p-7 relative"
            style={{
              backgroundColor: 'hsl(145 38% 90%)',
              boxShadow: '0 8px 32px hsl(145 40% 30% / 0.25), 0 2px 8px hsl(145 40% 30% / 0.15)',
              border: '1px solid hsl(145 35% 78%)',
            }}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => { setShowSignUp(false); setSignUpForSubscription(false); }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full transition-colors"
              style={{ color: '#416478' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 35% 80%)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
              aria-label="Close sign up"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <div className="mb-5">
              <h2 className="text-2xl font-bold mb-1" style={{ color: '#416478' }}>
                Create Account
              </h2>
              <p className="text-sm" style={{ color: '#416478', opacity: 0.75 }}>
                {signUpForSubscription ? 'Create your account to subscribe to PEAK Vault' : 'Join as a PEAK Member'}
              </p>
            </div>

            <form onSubmit={handleSignUpSubmit(onSignUp)} className="space-y-4" noValidate>
              {/* Full Name */}
              <div>
                <label className="label" htmlFor="signup-fullname" style={{ color: '#416478' }}>
                  Full Name
                </label>
                <input
                  id="signup-fullname"
                  type="text"
                  autoComplete="name"
                  className="input-field"
                  placeholder="Your full name"
                  {...registerSignUp('fullName', {
                    required: 'Full name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' },
                  })}
                  style={signUpErrors.fullName ? { borderColor: 'hsl(0 72% 55%)', boxShadow: '0 0 0 3px hsl(0 72% 50% / 0.12)' } : {}}
                />
                {signUpErrors.fullName && (
                  <p className="mt-1.5 text-xs font-medium" style={{ color: 'hsl(0 72% 60%)' }}>
                    {signUpErrors.fullName.message}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="label" htmlFor="signup-email" style={{ color: '#416478' }}>
                  Email Address
                </label>
                <input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  className="input-field"
                  placeholder="you@example.com"
                  {...registerSignUp('email', {
                    required: 'Email address is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
                  })}
                  style={signUpErrors.email ? { borderColor: 'hsl(0 72% 55%)', boxShadow: '0 0 0 3px hsl(0 72% 50% / 0.12)' } : {}}
                />
                {signUpErrors.email && (
                  <p className="mt-1.5 text-xs font-medium" style={{ color: 'hsl(0 72% 60%)' }}>
                    {signUpErrors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="label" htmlFor="signup-password" style={{ color: '#416478' }}>
                  Password
                </label>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showSignUpPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input-field pr-11"
                    placeholder="Choose a password"
                    {...registerSignUp('password', {
                      required: 'Password is required',
                      minLength: { value: 6, message: 'Password must be at least 6 characters' },
                    })}
                    style={signUpErrors.password ? { borderColor: 'hsl(0 72% 55%)', boxShadow: '0 0 0 3px hsl(0 72% 50% / 0.12)' } : {}}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors z-10"
                    style={{ color: '#416478' }}
                    tabIndex={-1}
                    aria-label={showSignUpPassword ? 'Hide password' : 'Show password'}
                  >
                    {showSignUpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {signUpErrors.password && (
                  <p className="mt-1.5 text-xs font-medium" style={{ color: 'hsl(0 72% 60%)' }}>
                    {signUpErrors.password.message}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="label" htmlFor="signup-confirm-password" style={{ color: '#416478' }}>
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="signup-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input-field pr-11"
                    placeholder="Confirm your password"
                    {...registerSignUp('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) => value === signUpPassword || 'Passwords do not match',
                    })}
                    style={signUpErrors.confirmPassword ? { borderColor: 'hsl(0 72% 55%)', boxShadow: '0 0 0 3px hsl(0 72% 50% / 0.12)' } : {}}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors z-10"
                    style={{ color: '#416478' }}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {signUpErrors.confirmPassword && (
                  <p className="mt-1.5 text-xs font-medium" style={{ color: 'hsl(0 72% 60%)' }}>
                    {signUpErrors.confirmPassword.message}
                  </p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSigningUp}
                className="btn-primary w-full py-3 text-base mt-2"
                style={{ minHeight: '48px' }}
              >
                {isSigningUp ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Creating account…
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              <span className="text-sm" style={{ color: '#416478' }}>
                Already have an account?{' '}
              </span>
              <button
                type="button"
                onClick={() => { setShowSignUp(false); setSignUpForSubscription(false); }}
                className="text-sm font-semibold underline underline-offset-2"
                style={{ color: '#416478' }}
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Confirmation Modal */}
      {pendingSignUpData && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
        >
          <div
            className="w-full max-w-sm rounded-2xl p-7 relative"
            style={{
              backgroundColor: 'hsl(145 38% 90%)',
              boxShadow: '0 8px 32px hsl(145 40% 30% / 0.30), 0 2px 8px hsl(145 40% 30% / 0.18)',
              border: '1px solid hsl(145 35% 78%)',
            }}
          >
            {/* Warning icon */}
            <div className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#416478" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h2 className="text-xl font-bold text-center mb-2" style={{ color: '#416478' }}>
              Confirm Your Email
            </h2>
            <p className="text-sm text-center mb-4" style={{ color: '#416478', opacity: 0.8 }}>
              Please check your email address is correct, it cannot be changed once your account is created.
            </p>

            {/* Email display */}
            <div
              className="rounded-xl px-4 py-3 mb-5 text-center"
              style={{ backgroundColor: 'hsl(145 35% 82%)', border: '1px solid hsl(145 35% 70%)' }}
            >
              <p className="text-xs font-medium mb-1" style={{ color: '#416478', opacity: 0.65 }}>Your email address</p>
              <p className="text-base font-bold break-all" style={{ color: '#416478' }}>
                {pendingSignUpData.email}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPendingSignUpData(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150"
                style={{
                  backgroundColor: 'transparent',
                  color: '#416478',
                  border: '2px solid hsl(145 35% 65%)',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 35% 82%)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={confirmAndCreateAccount}
                disabled={isSigningUp}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 flex items-center justify-center gap-2"
                style={{
                  backgroundColor: 'hsl(145 50% 35%)',
                  color: 'white',
                  border: '2px solid white',
                  outline: '1px solid hsl(145 55% 28%)',
                }}
                onMouseEnter={(e) => { if (!isSigningUp) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 55% 28%)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 50% 35%)'; }}
              >
                {isSigningUp ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Creating…
                  </>
                ) : (
                  'Yes, that\'s correct'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Upload Popup — shown immediately after signup */}
      {newUserUploader && (
        <SessionUploadPopup
          userId={newUserUploader.userId}
          userEmail={newUserUploader.userEmail}
          userName={newUserUploader.userName}
          onClose={() => setNewUserUploader(null)}
        />
      )}
    </div>
  );
}
